import React from 'react';
import { Box } from '@mui/material';
import { fontFamily } from '../tokens';

/**
 * Tag — the square-bullet pill from the reference (`■ Focused Analytics`,
 * `Requires Review`). A compact status/label chip with a leading square.
 *
 * Props:
 *   tone     'cobalt' | 'ink' | 'neutral' | 'success' | 'warning' | 'error'
 *   variant  'solid' | 'soft' | 'outline'  (default 'solid')
 *   bullet   boolean — leading square (default true)
 *   size     'sm' | 'md'  (default 'sm')
 */
// Each tone resolves: `main`/`on` for the solid block, plus `softBg`/`softText`
// where softText is an AA-contrast foreground on softBg (≥4.5:1 for the label).
// `ink` is pinned to fixed neutrals so the "black block" is stable in dark mode.
const TONES = {
  cobalt: (t) => ({
    main: t.palette.primary.main,
    on: t.palette.surface.text.onAccent,
    softBg: t.palette.surface.accentSoft,
    softText: t.palette.mode === 'dark' ? t.palette.primary.light : t.palette.primary.dark,
  }),
  ink: (t) => ({
    main: t.palette.neutral[900],
    on: '#FFFFFF',
    softBg: t.palette.surface.sunken,
    softText: t.palette.text.primary,
  }),
  neutral: (t) => ({
    main: t.palette.surface.borderStrong,
    on: t.palette.text.primary,
    softBg: t.palette.surface.sunken,
    softText: t.palette.text.secondary,
  }),
  success: (t) => ({ main: t.palette.success.main, on: '#FFFFFF', softBg: t.palette.success.light, softText: t.palette.success.dark }),
  warning: (t) => ({ main: t.palette.warning.main, on: t.palette.warning.contrastText, softBg: t.palette.warning.light, softText: '#6E5208' }),
  error: (t) => ({ main: t.palette.error.main, on: '#FFFFFF', softBg: t.palette.error.light, softText: t.palette.error.dark }),
};

function Tag({ tone = 'neutral', variant = 'solid', bullet = true, dot = false, size = 'sm', children, sx }) {
  const pad = size === 'md' ? { px: 1.25, py: 0.5, fontSize: 12 } : { px: 1, py: 0.375, fontSize: 11 };

  const style = (t) => {
    const c = (TONES[tone] || TONES.neutral)(t);
    if (variant === 'soft') return { backgroundColor: c.softBg, color: c.softText, border: 'none' };
    if (variant === 'outline')
      return { backgroundColor: 'transparent', color: c.softText, border: `1px solid ${c.main}` };
    return { backgroundColor: c.main, color: c.on, border: 'none' };
  };

  return (
    <Box
      component="span"
      sx={(t) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        borderRadius: t.tokens.radius.sm + 'px',
        fontFamily: fontFamily.mono,
        fontWeight: 500,
        lineHeight: 1.2,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        ...pad,
        ...style(t),
        ...(typeof sx === 'function' ? sx(t) : sx),
      })}
    >
      {bullet || dot ? (
        <Box
          aria-hidden
          component="span"
          sx={{
            width: 7,
            height: 7,
            flex: '0 0 auto',
            borderRadius: dot ? '50%' : 0,
            backgroundColor: 'currentColor',
            opacity: 0.95,
          }}
        />
      ) : null}
      {children}
    </Box>
  );
}

export default Tag;
